// فرم ثبت‌نام — تم Dark Pro (شیشه‌ای + گرادیان آبی/نیلی)، ریسپانسیو برای موبایل

import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { getLeagues } from "../services/leaguesService";

// -------------------------
//   SUPER DARK PRO THEME
// -------------------------

const CssTextField = styled(TextField)(() => ({
  '& label': {
    color: '#cbd5e1',
    fontFamily: 'fanoos',
    fontSize: '0.9rem',
  },
  '& label.Mui-focused': {
    color: '#38bdf8'
  },
  '& label.Mui-error': {
    color: '#f87171'
  },
  '& .MuiInputBase-root': {
    color: '#f8fafc',
    fontFamily: 'fanoos',
  },
  // Note: the app's emotion cache uses stylis-plugin-rtl, so MUI styles are auto-flipped for RTL
  '& .MuiInputBase-input': {
    textAlign: 'left',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  '& .MuiInput-underline:before': {
    borderBottomColor: '#475569'
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: '#7dd3fc'
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: '#38bdf8'
  },
  '& .MuiInput-underline.Mui-error:after': {
    borderBottomColor: '#f87171'
  },
  '& .MuiFormHelperText-root': {
    color: '#94a3b8',
    fontFamily: 'fanoos',
    fontSize: '0.75rem',
    textAlign: 'left',
    marginTop: '6px',
    lineHeight: 1.6,
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: '#f87171'
  },
}));

const FIELD_LABELS = {
  firstname: "نام",
  lastname: "نام خانوادگی",
  personalcode: "کد ملی",
  phone_number: "شماره موبایل",
  email: "ایمیل",
  password: "رمز عبور",
  teamname: "نام تیم",
  username: "نام کاربری",
  teampic: "لوگوی تیم",
  teamrole: "نقش در تیم",
  role: "نقش",
  axis: "محور",
  league: "لیگ",
};

// ترتیب نمایش فیلدها در فرم (برای اسکرول به اولین خطا)
const FIELD_ORDER = ["firstname", "lastname", "personalcode", "phone_number", "email", "username", "password", "teamname", "teampic", "axis", "league"];
const REQUIRED_FIELDS = ["firstname", "lastname", "personalcode", "phone_number", "email", "username", "password", "teamname", "axis", "league"];
const MAX_LOGO_SIZE = 5 * 1024 * 1024; // 5MB

const toEnglishDigits = (str) =>
  String(str ?? "")
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));

const formatSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} مگابایت` : `${Math.max(1, Math.round(bytes / 1024))} کیلوبایت`;

function validateLogo(file) {
  if (!file) return null;
  if (file.type && !file.type.startsWith("image/")) return "لوگوی تیم باید یک فایل تصویری (مثل JPG یا PNG) باشد.";
  if (file.size > MAX_LOGO_SIZE) return "حجم لوگو نباید بیشتر از ۵ مگابایت باشد.";
  return null;
}

// Returns { field: message } for every invalid field
function validateForm(data) {
  const errors = {};
  REQUIRED_FIELDS.forEach((field) => {
    if (!String(data[field] ?? "").trim()) {
      errors[field] = field === "axis" || field === "league" ? `${FIELD_LABELS[field]} را انتخاب کنید.` : `${FIELD_LABELS[field]} الزامی است.`;
    }
  });

  const email = String(data.email).trim();
  if (!errors.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = "ایمیل وارد شده معتبر نیست. نمونه صحیح: name@example.com";
  }
  const nationalCode = toEnglishDigits(data.personalcode).trim();
  if (!errors.personalcode && !/^\d{10}$/.test(nationalCode)) {
    errors.personalcode = "کد ملی باید ۱۰ رقم و فقط شامل عدد باشد.";
  }
  const phone = toEnglishDigits(data.phone_number).trim();
  if (!errors.phone_number && !/^09\d{9}$/.test(phone)) {
    errors.phone_number = "شماره موبایل باید ۱۱ رقم باشد و با ۰۹ شروع شود.";
  }
  if (!errors.password && String(data.password).length < 8) {
    errors.password = "رمز عبور باید حداقل ۸ کاراکتر باشد.";
  }
  const logoError = validateLogo(data.teampic);
  if (logoError) errors.teampic = logoError;
  return errors;
}

async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

// Translate common Django/DRF (English) validation messages to Persian
function translateBackendMessage(field, msg) {
  const m = String(msg || "");
  const label = FIELD_LABELS[field] || field;
  if (/already exists/i.test(m)) {
    if (field === "email") return "این ایمیل قبلاً ثبت‌نام کرده است. اگر رمز خود را فراموش کرده‌اید از «فراموشی رمز عبور» استفاده کنید.";
    if (field === "personalcode") return "این کد ملی قبلاً ثبت‌نام کرده است.";
    return `${label} تکراری است و قبلاً ثبت شده است.`;
  }
  if (/valid email/i.test(m)) return "ایمیل وارد شده معتبر نیست.";
  if (/may not be blank|is required|may not be null/i.test(m)) return `${label} الزامی است.`;
  if (/no more than (\d+) characters/i.test(m)) {
    const n = m.match(/no more than (\d+) characters/i)[1];
    return `${label} نباید بیشتر از ${n} کاراکتر باشد.`;
  }
  if (/valid image|not a file|corrupted image/i.test(m)) return "لوگوی تیم باید یک فایل تصویری معتبر (JPG یا PNG) باشد.";
  if (/invalid pk|does not exist/i.test(m)) return `${label} انتخاب‌شده معتبر نیست.`;
  if (/too short/i.test(m)) return `${label} خیلی کوتاه است.`;
  if (/too common/i.test(m)) return "رمز عبور خیلی ساده است. رمز قوی‌تری انتخاب کنید.";
  if (/entirely numeric/i.test(m)) return "رمز عبور نباید فقط عدد باشد.";
  if (/too similar/i.test(m)) return "رمز عبور خیلی شبیه اطلاعات شخصی شماست.";
  // Already Persian or unknown: show with field label
  return /[؀-ۿ]/.test(m) ? `${label}: ${m}` : `${label}: مقدار وارد شده معتبر نیست.`;
}

// Per-field errors from a DRF 400 response
function backendFieldErrors(data) {
  const errors = {};
  if (!data || typeof data !== "object") return errors;
  Object.entries(data).forEach(([field, value]) => {
    if (!FIELD_LABELS[field]) return;
    const first = Array.isArray(value) ? value[0] : value;
    errors[field] = translateBackendMessage(field, first);
  });
  return errors;
}

function signupErrorMessage(status, data) {
  if (status === 403) return data?.message || "ثبت‌نام در حال حاضر غیرفعال است.";
  if (status === 413) return "حجم فایل لوگو بیش از حد مجاز است. لطفاً تصویر کم‌حجم‌تری انتخاب کنید.";
  if (status === 429) return "تعداد درخواست‌ها زیاد است. لطفاً چند دقیقه دیگر دوباره تلاش کنید.";
  if (status >= 500) return "خطای سرور رخ داد. لطفاً کمی بعد دوباره تلاش کنید یا با پشتیبانی تماس بگیرید.";

  if (data && typeof data === "object") {
    if (typeof data.message === "string") return data.message;
    if (typeof data.detail === "string") return data.detail;
    const messages = [];
    Object.entries(data).forEach(([field, value]) => {
      const list = Array.isArray(value) ? value : [value];
      list.forEach((msg) => {
        if (field === "non_field_errors") messages.push(String(msg));
        else messages.push(translateBackendMessage(field, msg));
      });
    });
    if (messages.length > 0) return messages.join("\n");
  }
  return "ثبت‌نام انجام نشد. لطفاً اطلاعات را بررسی کرده و دوباره تلاش کنید.";
}

// -------------------------
//   UI building blocks
// -------------------------

function Section({ step, title, subtitle, children }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-sm font-bold text-white shadow-[0_0_12px_rgba(56,189,248,0.45)]">
          {step}
        </span>
        <div className="text-right">
          <h2 className="text-base sm:text-lg font-bold text-slate-100 leading-tight">{title}</h2>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function SelectField({ id, label, error, children, ...props }) {
  return (
    <div data-field={props.name}>
      <label htmlFor={id} className={`mb-2 block text-sm ${error ? "text-red-400" : "text-slate-300"}`}>
        {label} <span className="text-sky-400">*</span>
      </label>
      <div className="relative">
        <select
          id={id}
          {...props}
          className={`w-full appearance-none rounded-xl border bg-slate-900/80 py-3 pr-4 pl-10 text-sm text-slate-100 outline-none transition focus:ring-2 focus:ring-sky-500/60
            ${error ? "border-red-400/70" : "border-slate-700 hover:border-sky-400/60 focus:border-sky-500"}
            ${props.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
        >
          {children}
        </select>
        <svg className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </div>
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  );
}

function LogoDropzone({ file, onFile, error }) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(null);
  const dragDepth = useRef(0);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const openPicker = () => inputRef.current?.click();

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    setDragActive(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setDragActive(false);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setDragActive(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) onFile(dropped);
  };
  const handleInput = (e) => {
    const picked = e.target.files?.[0];
    if (picked) onFile(picked);
    e.target.value = ""; // allow picking the same file again after removing it
  };

  return (
    <div data-field="teampic">
      <span className={`mb-2 block text-sm ${error ? "text-red-400" : "text-slate-300"}`}>
        لوگوی تیم <span className="text-slate-500 text-xs">(اختیاری)</span>
      </span>

      <input ref={inputRef} type="file" accept="image/*" onChange={handleInput} className="hidden" />

      {file && preview ? (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`flex items-center gap-4 rounded-2xl border p-3 sm:p-4 transition
            ${dragActive ? "border-sky-400 bg-sky-500/10" : error ? "border-red-400/70 bg-red-500/5" : "border-white/10 bg-slate-900/60"}`}
        >
          <img src={preview} alt="پیش‌نمایش لوگو" className="h-16 w-16 sm:h-20 sm:w-20 shrink-0 rounded-xl object-cover ring-2 ring-sky-500/40 bg-slate-800" />
          <div className="min-w-0 flex-1 text-right">
            <p className="truncate text-sm text-slate-100" dir="ltr" style={{ textAlign: "right" }}>{file.name}</p>
            <p className="mt-1 text-xs text-slate-400">{formatSize(file.size)}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" onClick={openPicker} className="rounded-lg border border-sky-500/50 px-3 py-1.5 text-xs text-sky-300 transition hover:bg-sky-500/10">
                تغییر لوگو
              </button>
              <button type="button" onClick={() => onFile(null)} className="rounded-lg border border-red-400/40 px-3 py-1.5 text-xs text-red-300 transition hover:bg-red-500/10">
                حذف
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={openPicker}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openPicker();
            }
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`group flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-4 py-7 sm:py-9 text-center outline-none transition
            focus-visible:ring-2 focus-visible:ring-sky-500/60
            ${dragActive
              ? "scale-[1.01] border-sky-400 bg-sky-500/10 shadow-[0_0_25px_rgba(56,189,248,0.35)]"
              : error
                ? "border-red-400/70 bg-red-500/5"
                : "border-slate-600 bg-slate-900/50 hover:border-sky-400/70 hover:bg-sky-500/5"}`}
        >
          <span className={`flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-[0_0_20px_rgba(56,189,248,0.4)] transition ${dragActive ? "animate-bounce" : "group-hover:scale-110"}`}>
            <CloudUploadIcon />
          </span>
          <div>
            <p className="text-sm text-slate-100">
              {dragActive ? "لوگو را اینجا رها کنید" : (
                <>
                  <span className="hidden sm:inline">لوگو را اینجا بکشید و رها کنید یا </span>
                  <span className="font-bold text-sky-400">انتخاب فایل</span>
                </>
              )}
            </p>
            <p className="mt-1 text-xs text-slate-400">JPG، PNG یا WEBP — حداکثر ۵ مگابایت</p>
          </div>
        </div>
      )}
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  );
}

// -------------------------
//   Signup form
// -------------------------

export default function BasicTextFields() {
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [leagues, setLeagues] = useState([]);
  const [leaguesLoading, setLeaguesLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    personalcode: "",
    phone_number: "",
    teamname: "",
    password: "",
    email: "",
    teampic: null,
    teamrole: "Leader",
    username: "",
    role: "User",
    axis: "",
    league: "",
  });

  const showError = (message) =>
    enqueueSnackbar(message, {
      variant: "error",
      autoHideDuration: 8000,
      style: { whiteSpace: "pre-line" },
      action: (key) => (
        <IconButton onClick={() => closeSnackbar(key)}>
          <CloseIcon style={{ color: "white" }} />
        </IconButton>
      ),
    });

  useEffect(() => {
    let mounted = true;
    getLeagues()
      .then((data) => {
        if (mounted) setLeagues(data);
      })
      .catch(() => {
        enqueueSnackbar("خطا در بارگذاری لیگ ها", { variant: "error" });
      })
      .finally(() => {
        if (mounted) setLeaguesLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [enqueueSnackbar]);

  const clearError = (name) => {
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleLogo = (file) => {
    const logoError = validateLogo(file);
    if (logoError) {
      setErrors((prev) => ({ ...prev, teampic: logoError }));
      showError(logoError);
      return;
    }
    setFormData((prev) => ({ ...prev, teampic: file }));
    clearError("teampic");
  };

  const focusFirstError = (errs) => {
    const first = FIELD_ORDER.find((f) => errs[f]);
    if (!first) return;
    const el = document.querySelector(`[data-field="${first}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.querySelector("input:not([type=file]), select")?.focus({ preventScroll: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // 1) Client-side validation
    const clientErrors = validateForm(formData);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      focusFirstError(clientErrors);
      const count = Object.keys(clientErrors).length;
      showError(count === 1 ? Object.values(clientErrors)[0] : `${count} مورد در فرم نیاز به اصلاح دارد. موارد با رنگ قرمز مشخص شده‌اند.`);
      return;
    }

    setLoading(true);
    try {
      // 2) Check if team already exists
      let checkRes;
      try {
        checkRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/check-team/?teamname=${encodeURIComponent(formData.teamname)}&league=${encodeURIComponent(formData.league)}`
        );
      } catch {
        throw new Error("ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید.");
      }
      const checkData = await safeJson(checkRes);
      if (checkData?.exists) {
        const msg = "تیمی با این نام در لیگ انتخابی ثبت شده است. لطفاً نام تیم دیگری انتخاب کنید.";
        setErrors({ teamname: msg });
        focusFirstError({ teamname: msg });
        throw new Error(msg);
      }

      // 3) Create FormData (skip empty values, e.g. no team logo selected)
      const form = new FormData();
      Object.keys(formData).forEach((key) => {
        const value = formData[key];
        if (value === null || value === undefined || value === "") return;
        if (key === "personalcode" || key === "phone_number") {
          form.append(key, toEnglishDigits(value).trim());
        } else {
          form.append(key, typeof value === "string" ? value.trim() : value);
        }
      });

      // 4) Signup
      let signupRes;
      try {
        signupRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/signup/`, {
          method: "POST",
          body: form,
          headers: { Accept: "application/json" },
        });
      } catch {
        throw new Error("ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید.");
      }

      const signupData = await safeJson(signupRes);

      if (!signupRes.ok) {
        if (signupRes.status === 400) {
          const fieldErrors = backendFieldErrors(signupData);
          setErrors(fieldErrors);
          focusFirstError(fieldErrors);
        }
        throw new Error(signupErrorMessage(signupRes.status, signupData));
      }

      // 5) Success
      enqueueSnackbar("ثبت‌نام با موفقیت انجام شد! اکنون می‌توانید وارد شوید.", { variant: "success" });
      setTimeout(() => navigate("/LogIn"), 1500);
    } catch (err) {
      showError(err.message || "خطایی رخ داد");
    }
    setLoading(false);
  };

  // axes list (parent leagues) and leagues filtered by selected axis
  // only include axes that have at least one active sub_league
  const axes = (leagues || []).filter((a) => (a.sub_leagues || []).some((l) => l.league_type === "active"));

  // if selected axis is no longer valid (e.g., after leagues update), clear it
  useEffect(() => {
    const stillValid = (leagues || []).some(
      (a) => String(a.id) === String(formData.axis) && (a.sub_leagues || []).some((l) => l.league_type === "active")
    );
    if (formData.axis && !stillValid) {
      setFormData((prev) => ({ ...prev, axis: "", league: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagues]);

  const leaguesForSelectedAxis = formData.axis
    ? (axes.find((a) => String(a.id) === String(formData.axis))?.sub_leagues || []).filter((l) => l.league_type === "active")
    : [];

  // Shared props for text inputs
  const field = (name, extra = {}) => {
    const { helper, ltr, inputMode, maxLength, autoComplete, type, InputProps } = extra;
    return {
      name,
      label: FIELD_LABELS[name],
      required: REQUIRED_FIELDS.includes(name),
      type: type || "text",
      value: formData[name],
      onChange: handleChange,
      error: Boolean(errors[name]),
      helperText: errors[name] || helper || " ",
      variant: "standard",
      fullWidth: true,
      autoComplete,
      InputProps,
      inputProps: {
        inputMode,
        maxLength,
        dir: ltr ? "ltr" : "rtl",
        style: ltr ? { textAlign: "right" } : undefined,
      },
    };
  };

  return (
    <div className="w-full flex justify-center items-start py-6 sm:py-10 px-3 sm:px-4 text-base" dir="rtl">
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit}
        className="w-full max-w-3xl bg-white/5 backdrop-blur-xl border border-white/10 shadow-[0_0_40px_rgba(0,0,0,0.7)] rounded-3xl p-4 sm:p-10 text-white font-fanoos"
      >
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-wide bg-gradient-to-r from-sky-400 to-indigo-400 bg-clip-text text-transparent drop-shadow-lg leading-relaxed">
            ثبت‌نام لیگ‌های کارون کاپ
          </h1>
          <p className="mt-2 text-xs sm:text-sm text-slate-400">
            فیلدهای دارای <span className="text-sky-400">*</span> الزامی هستند
          </p>
        </div>

        <div className="flex flex-col gap-4 sm:gap-6">
          {/* 1) اطلاعات فردی */}
          <Section step="۱" title="اطلاعات سرپرست تیم" subtitle="مشخصات فردی سرپرست (لیدر) تیم">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              <div data-field="firstname"><CssTextField {...field("firstname", { autoComplete: "given-name" })} /></div>
              <div data-field="lastname"><CssTextField {...field("lastname", { autoComplete: "family-name" })} /></div>
              <div data-field="personalcode">
                <CssTextField {...field("personalcode", { ltr: true, inputMode: "numeric", maxLength: 10, helper: "۱۰ رقم، بدون خط تیره" })} />
              </div>
              <div data-field="phone_number">
                <CssTextField {...field("phone_number", { ltr: true, inputMode: "tel", maxLength: 11, autoComplete: "tel", helper: "مثال: 09123456789" })} />
              </div>
            </div>
          </Section>

          {/* 2) اطلاعات حساب */}
          <Section step="۲" title="اطلاعات حساب کاربری" subtitle="برای ورود به سایت از ایمیل و رمز عبور استفاده می‌شود">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              <div data-field="email" className="sm:col-span-2">
                <CssTextField {...field("email", { type: "email", ltr: true, inputMode: "email", autoComplete: "email", helper: "ایمیل به حروف کوچک و بزرگ حساس است" })} />
              </div>
              <div data-field="username"><CssTextField {...field("username", { ltr: true, autoComplete: "username" })} /></div>
              <div data-field="password">
                <CssTextField
                  {...field("password", {
                    type: showPassword ? "text" : "password",
                    ltr: true,
                    autoComplete: "new-password",
                    helper: "حداقل ۸ کاراکتر — به حروف کوچک و بزرگ حساس است",
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setShowPassword((s) => !s)}
                            aria-label={showPassword ? "پنهان کردن رمز عبور" : "نمایش رمز عبور"}
                            sx={{ color: "#94a3b8", "&:hover": { color: "#38bdf8" } }}
                          >
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  })}
                />
              </div>
            </div>
          </Section>

          {/* 3) اطلاعات تیم */}
          <Section step="۳" title="اطلاعات تیم و لیگ" subtitle="تیم خود را معرفی کرده و لیگ مورد نظر را انتخاب کنید">
            <div className="flex flex-col gap-5">
              <div data-field="teamname"><CssTextField {...field("teamname")} /></div>

              <LogoDropzone file={formData.teampic} onFile={handleLogo} error={errors.teampic} />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <SelectField
                  id="signup-axis"
                  label="انتخاب محور"
                  name="axis"
                  value={formData.axis}
                  error={errors.axis}
                  onChange={(e) => { handleChange(e); setFormData((prev) => ({ ...prev, league: "" })); }}
                >
                  <option value="" disabled>
                    {leaguesLoading ? "در حال بارگذاری..." : axes.length ? "— محور را انتخاب کنید —" : "محور فعالی وجود ندارد"}
                  </option>
                  {axes.map((axis) => (
                    <option key={axis.id} value={axis.id}>{axis.name}</option>
                  ))}
                </SelectField>

                <SelectField
                  id="signup-league"
                  label="انتخاب لیگ"
                  name="league"
                  value={formData.league}
                  error={errors.league}
                  onChange={handleChange}
                  disabled={!formData.axis}
                >
                  <option value="" disabled>
                    {!formData.axis
                      ? "ابتدا محور را انتخاب کنید"
                      : leaguesForSelectedAxis.length
                        ? "— لیگ را انتخاب کنید —"
                        : "هیچ لیگ فعالی در این محور وجود ندارد"}
                  </option>
                  {leaguesForSelectedAxis.map((league) => (
                    <option key={league.id} value={league.id}>
                      {league.name}
                      {league.parent_league ? ` (${league.parent_league.name})` : ""}
                    </option>
                  ))}
                </SelectField>
              </div>
            </div>
          </Section>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`mt-6 sm:mt-8 w-full py-3.5 flex items-center justify-center gap-3 rounded-xl
            bg-gradient-to-r from-sky-500 to-indigo-600 text-white text-base sm:text-lg font-fanoos
            shadow-[0_0_20px_rgba(56,189,248,0.4)]
            transition ${loading ? "opacity-70 cursor-not-allowed" : "hover:brightness-110 active:scale-[0.99]"}`}
        >
          {loading ? (
            <>
              <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
              درحال انجام ثبت نام...
            </>
          ) : (
            "ثبت نام"
          )}
        </button>

        <p className="mt-5 text-center text-sm text-slate-400">
          قبلاً ثبت‌نام کرده‌اید؟{" "}
          <a href="/LogIn" className="font-semibold text-sky-400 hover:text-sky-300">
            وارد شوید
          </a>
        </p>
      </Box>
    </div>
  );
}
